import type { IndividualPaymentPersonViewModel } from '@/shared/api/generated';

/** Listagem de alunos já normalizada, com metadados de paginação quando existirem. */
export interface ParsedPeopleResponse {
  readonly people: readonly IndividualPaymentPersonViewModel[];
  /** Total de registros informado pelo backend; `null` quando não vem no payload. */
  readonly totalCount: number | null;
  /** Total de páginas informado pelo backend; `null` quando não vem no payload. */
  readonly totalPages: number | null;
}

const EMPTY: ParsedPeopleResponse = { people: [], totalCount: null, totalPages: null };

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Normaliza a resposta de `GET /api/v1/individualpayment/people`.
 *
 * O Kubb declara a resposta como `IndividualPaymentPersonViewModel[]`, mas o
 * backend pode envelopar em `{ items | data | people, totalCount, totalPages }`
 * — o mesmo desencontro que `unwrapPagedResponse` e `parseGridResponse`
 * (em `useEnrollmentGrid`) já tratam em outras telas. Chamar `.map` direto no
 * `data` quebra em runtime quando o envelope aparece.
 *
 * Quando o envelope traz `totalCount`/`totalPages`, os valores são repassados e
 * a paginação deixa de ser estimada pelo tamanho da página.
 *
 * @param data - Corpo da resposta, em qualquer um dos formatos.
 * @param pageSize - Usado para derivar `totalPages` quando só `totalCount` vem.
 *
 * @example
 * ```ts
 * parsePeopleResponse([{ id: 1 }], 50);
 * // { people: [{ id: 1 }], totalCount: null, totalPages: null }
 *
 * parsePeopleResponse({ items: [{ id: 1 }], totalCount: 96 }, 50);
 * // { people: [{ id: 1 }], totalCount: 96, totalPages: 2 }
 * ```
 */
export function parsePeopleResponse(data: unknown, pageSize: number): ParsedPeopleResponse {
  if (Array.isArray(data)) {
    return {
      people: data as readonly IndividualPaymentPersonViewModel[],
      totalCount: null,
      totalPages: null,
    };
  }

  if (data === null || typeof data !== 'object') return EMPTY;

  const obj = data as Record<string, unknown>;
  const listKey = (['items', 'data', 'people', 'result'] as const).find(key =>
    Array.isArray(obj[key]),
  );
  const people = listKey
    ? (obj[listKey] as readonly IndividualPaymentPersonViewModel[])
    : ([] as readonly IndividualPaymentPersonViewModel[]);

  const totalCount =
    numberOrNull(obj['totalCount']) ??
    numberOrNull(obj['totalPeopleCount']) ??
    numberOrNull(obj['total']);

  const totalPages =
    numberOrNull(obj['totalPages']) ??
    numberOrNull(obj['pageCount']) ??
    (totalCount !== null && pageSize > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : null);

  return { people, totalCount, totalPages };
}
